/**
 * staff-list-lib.ts — Phần THUẦN của danh sách nhân viên AI (`/ai/nhan-vien`): thứ tự thẻ, dòng kênh phụ trách,
 * chế độ lúc này, dòng model + nguồn.
 */
import type { AiBot, AiMode } from '@/hooks/use-ai'
import { AI_MODE_LABEL, isDefaultBot } from '@/lib/ai-khung'
import { botSourceLine } from '@/lib/ai-credits'
import { scheduleModeNow } from '@/components/ai/work-hours-lib'
import { i18n } from '@/lib/i18n'

/** Nhân viên AI mặc định đứng đầu, còn lại giữ thứ tự máy chủ (tạo trước đứng trước). */
export function sortStaff<T extends Pick<AiBot, 'isDefault' | 'allChannels'>>(bots: T[]): T[] {
  return [...bots.filter((b) => isDefaultBot(b)), ...bots.filter((b) => !isDefaultBot(b))]
}

/**
 * Kênh phụ trách: ưu tiên `status.channels` (kênh bot thực sự phục vụ); không có thì theo giao riêng.
 * Bot mặc định: "Mọi kênh chưa giao cho ai".
 */
export function staffChannelsText(
  bot: Pick<AiBot, 'isDefault' | 'allChannels' | 'channelAccountIds' | 'status'>,
  channelName: (id: string) => string | null,
): { names: string[]; summary: string } {
  const served = bot.status?.channels
  const names = served
    ? served.map((c) => c.name || channelName(c.id) || i18n.t('aiStaff:lib.listLib.unnamedChannel'))
    : (bot.channelAccountIds ?? []).map((id) => channelName(id) || i18n.t('aiStaff:lib.listLib.removedChannel'))
  if (isDefaultBot(bot)) return { names, summary: i18n.t('aiStaff:lib.listLib.defaultSummary') + (served ? i18n.t('aiStaff:lib.listLib.servedCount', { count: served.length }) : '') }
  return { names, summary: names.length ? i18n.t('aiStaff:lib.listLib.channelCount', { count: names.length }) : i18n.t('aiStaff:lib.listLib.noChannels') }
}

const asMode = (v: unknown): AiMode | null => (v === 'manual' || v === 'suggest' || v === 'auto' ? v : null)

/** Chế độ lúc này: theo `status.mode` của máy chủ; server cũ → tính từ lịch của bot. */
export function staffModeNow(bot: Pick<AiBot, 'status' | 'scheduleEnabled' | 'scheduleStartHour' | 'scheduleEndHour' | 'scheduleDaytimeMode' | 'scheduleNighttimeMode' | 'defaultMode'>, now = new Date()): string {
  const m = asMode(bot.status?.mode)
  if (m) return AI_MODE_LABEL[m]
  return AI_MODE_LABEL[scheduleModeNow({
    scheduleEnabled: !!bot.scheduleEnabled,
    scheduleStartHour: bot.scheduleStartHour,
    scheduleEndHour: bot.scheduleEndHour,
    scheduleDaytimeMode: bot.scheduleDaytimeMode,
    scheduleNighttimeMode: bot.scheduleNighttimeMode,
    defaultMode: asMode(bot.defaultMode) ?? 'suggest',
  }, now).mode]
}

/** "gpt-5.4-mini · AI của eCDP · 1,7 credit/lượt" / "Key của tôi" / kết nối ngoài. */
export function staffModelText(bot: Pick<AiBot, 'kind' | 'provider' | 'model' | 'effective'>, kindLabel: string): string {
  if (bot.kind !== 'builtin') return kindLabel
  const e = bot.effective
  const model = e?.model ?? bot.model
  const src = botSourceLine(e)?.label
  return [model || i18n.t('aiStaff:lib.listLib.noModel'), src].filter(Boolean).join(' · ')
}
